import DeleteIcon from "@mui/icons-material/Delete";
import { Autocomplete, IconButton } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import Colors from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useFormik } from "formik";
import { api } from "helpers/api_url";
import { useCallback, useEffect, useState } from "react";
import { Button, Container, Spinner, Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

interface FormValues {
  organisation: any;
  department: any;
  team: any;
  user: any;
}

const ConfigureWarehouse = () => {
  document.title = "Configure Warehouse";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [organisationOptions, setOrganisationOptions] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [teamOptions, setTeamOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [listToggle, setListToggle] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [autoSelectionLoading, setAutoSelectionLoading] = useState(false);
  const [organisationDropdownLoading, setOrganisationDropdownLoading] =
    useState(false);
  const [departmentDropdownLoading, setDepartmentDropdownLoading] =
    useState(false);
  const [teamDropdownLoading, setTeamDropdownLoading] = useState(false);
  const [userDropdownLoading, setUserDropdownLoading] = useState(false);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      organisation: null,
      department: null,
      team: null,
      user: null,
    },
    validationSchema: Yup.object({
      organisation: Yup.object()
        .nullable()
        .required("Organisation Name is required"),
      department: Yup.object()
        .nullable()
        .required("Department Name is required"),
      team: Yup.object().nullable().required("Team Name is required"),
      user: Yup.object().nullable().required("User Name is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        warehouse_id: id,
        user_id: values?.user?.user_id,
      };
      try {
        setLoading(true);
        if (id) {
          await Axios.post(api?.master?.configure_warehouse?.create, payload);
          toast.success("Warehouse Configured Successfully");
          setListToggle((prev) => !prev);
          formik?.resetForm();
          setDepartmentOptions([]);
          setTeamOptions([]);
          setUserOptions([]);
          // Re-initialize auto-selection for the next user
          initializeAutoSelection();
        }
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchTeamOptions = useCallback((department_id: string, autoSelect: boolean = true) => {
    setTeamDropdownLoading(true);
    Axios.get(
      `${api?.master?.warehouse?.configure_team_dropdown}/${department_id}`
    )
      .then((result) => {
        const options = result?.data?.data || [];
        setTeamOptions(options);

        // Auto-select the last team if autoSelect is true
        if (autoSelect && options.length > 0) {
          const lastTeam = options[options.length - 1];
          formik.setFieldValue("team", lastTeam);
          // Fetch users for the selected team
          fetchUsersOptions(lastTeam?.team_id);
          // Auto-selection is complete, hide loading
          setAutoSelectionLoading(false);
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch team dropdown");
        if (autoSelect) {
          setAutoSelectionLoading(false);
        }
      })
      .finally(() => {
        setTeamDropdownLoading(false);
      });
  }, [formik]);

  const fetchDepartmentOptions = useCallback((organization_id: string, autoSelect: boolean = true) => {
    setDepartmentDropdownLoading(true);
    Axios.get(
      `${api?.master?.warehouse?.configure_department_dropdown}?organization_id=${organization_id}`
    )
      .then((result) => {
        const options = result?.data?.data || [];
        setDepartmentOptions(options);

        // Auto-select the last department if autoSelect is true
        if (autoSelect && options.length > 0) {
          const lastDepartment = options[options.length - 1];
          formik.setFieldValue("department", lastDepartment);
          // Fetch teams for the selected department (keep loading state)
          fetchTeamOptions(lastDepartment?.department_id);
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch department dropdown");
        if (autoSelect) {
          setAutoSelectionLoading(false);
        }
      })
      .finally(() => {
        setDepartmentDropdownLoading(false);
      });
  }, [formik, fetchTeamOptions]);

  const initializeAutoSelection = useCallback(() => {
    setOrganisationDropdownLoading(true);
    Axios.get(`${api?.master?.warehouse?.configure_organisation_dropdown}`)
      .then((result) => {
        const options = result?.data?.data || [];
        setOrganisationOptions(options);

        // Auto-select the last organisation
        if (options.length > 0) {
          // Start auto-selection loading
          setAutoSelectionLoading(true);
          const lastOrganisation = options[options.length - 1];
          formik.setFieldValue("organisation", lastOrganisation);
          // Fetch departments for the selected organisation
          fetchDepartmentOptions(lastOrganisation?.organization_id);
        }
      })
      .catch((err) => {
        toast.error("Failed to fetch organisation dropdown");
        setAutoSelectionLoading(false);
      })
      .finally(() => {
        setOrganisationDropdownLoading(false);
      });
  }, [formik, fetchDepartmentOptions]);

  useEffect(() => {
    initializeAutoSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsersOptions = (team_id: string) => {
    setUserDropdownLoading(true);
    Axios.get(`${api?.master?.warehouse?.configure_user_dropdown}/${team_id}`)
      .then((result) => {
        setUserOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch user dropdown");
      })
      .finally(() => {
        setUserDropdownLoading(false);
      });
  };

  useEffect(() => {
    if (id) {
      setListLoading(true);
      Axios.get(`${api?.master?.configure_warehouse?.list}?warehouse_id=${id}`)
        .then((result) => {
          setList(result?.data?.data || []);
        })
        .catch((err) => {
          toast.error("Failed to fetch configure warehouse list");
        })
        .finally(() => {
          setListLoading(false);
        });
    }
  }, [id, listToggle]);

  const handleDelete = async (configure_warehouse_id: string) => {
    if (configure_warehouse_id) {
      setDeleteLoading(configure_warehouse_id);
      try {
        await Axios.delete(
          `${api?.master?.configure_warehouse?.delete}/${configure_warehouse_id}`
        );
        toast.success("User configuration deleted successfully");
        setListToggle((prev) => !prev);
      } catch (err) {
        toast.error("Failed to delete user configured with warehouse");
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  return (
    <PageContainer>
      <FormHeader
        title="Configure Warehouse"
        firstLabel="Warehouse"
        secondLabel="Configure Warehouse"
        handleArrowClick={() => navigate("/warehouse-list")}
      />

      {/* Table */}
      <div className="mb-4" style={{ position: "relative" }}>
        {autoSelectionLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
          >
            <LoadingSpinner label="Loading Users..." />
          </div>
        )}
        <Table responsive className="align-middle">
          <thead className="table-light border-bottom">
            <tr>
              <th style={{ width: 70 }} className="py-3 px-3">
                Sr No.
              </th>
              <th className="py-3 px-3">User</th>
              <th
                style={{ width: 150, textAlign: "center" }}
                className="py-3 px-3"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Input Row */}
            <tr>
              <td className="py-2 px-3"></td>
              {/* Hidden Organization Dropdown - Commented out but kept for reference */}
              {/* <td className="py-2 px-3">
                <Autocomplete
                  size="small"
                  options={organisationOptions || []}
                  autoHighlight
                  loading={organisationDropdownLoading}
                  disabled={organisationDropdownLoading}
                  value={formik?.values?.organisation}
                  onChange={(_, val) => {
                    formik.setFieldValue("organisation", val);
                    if (val) {
                      fetchDepartmentOptions(val?.organization_id, false);
                    } else {
                      setDepartmentOptions([]);
                      setTeamOptions([]);
                      setUserOptions([]);
                      formik.setFieldValue("department", null);
                      formik.setFieldValue("team", null);
                      formik.setFieldValue("user", null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("organisation", true)}
                  getOptionLabel={(option) =>
                    option ? option?.organization_name : ""
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => (opt ? opt?.organization_name : "")
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Organisation"
                      sx={autocompleteDropdownStyles.inputSx}
                      error={
                        formik.touched.organisation &&
                        Boolean(formik.errors.organisation)
                      }
                      helperText={
                        formik.touched.organisation &&
                        typeof formik.errors.organisation === "string"
                          ? formik.errors.organisation
                          : undefined
                      }
                    />
                  )}
                />
              </td> */}
              {/* Hidden Department Dropdown - Commented out but kept for reference */}
              {/* <td className="py-2 px-3">
                <Autocomplete
                  size="small"
                  options={departmentOptions || []}
                  autoHighlight
                  loading={departmentDropdownLoading}
                  disabled={
                    departmentDropdownLoading || !formik?.values?.organisation
                  }
                  value={formik?.values?.department}
                  onChange={(_, val) => {
                    formik.setFieldValue("department", val);
                    if (val) {
                      fetchTeamOptions(val?.department_id, false);
                    } else {
                      setTeamOptions([]);
                      setUserOptions([]);
                      formik.setFieldValue("team", null);
                      formik.setFieldValue("user", null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("department", true)}
                  getOptionLabel={(option) =>
                    option ? option?.department_name : ""
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => (opt ? opt?.department_name : "")
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Department"
                      sx={autocompleteDropdownStyles.inputSx}
                      error={
                        formik.touched.department &&
                        Boolean(formik.errors.department)
                      }
                      helperText={
                        formik.touched.department &&
                        typeof formik.errors.department === "string"
                          ? formik.errors.department
                          : undefined
                      }
                    />
                  )}
                />
              </td> */}
              {/* Hidden Team Dropdown - Commented out but kept for reference */}
              {/* <td className="py-2 px-3">
                <Autocomplete
                  size="small"
                  options={teamOptions || []}
                  autoHighlight
                  loading={teamDropdownLoading}
                  disabled={teamDropdownLoading || !formik?.values?.department}
                  value={formik?.values?.team}
                  onChange={(_, val) => {
                    formik.setFieldValue("team", val);
                    if (val) {
                      fetchUsersOptions(val?.team_id);
                    } else {
                      setUserOptions([]);
                      formik.setFieldValue("user", null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("team", true)}
                  getOptionLabel={(option) => (option ? option?.team_name : "")}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => (opt ? opt?.team_name : "")
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Team"
                      sx={autocompleteDropdownStyles.inputSx}
                      error={formik.touched.team && Boolean(formik.errors.team)}
                      helperText={
                        formik.touched.team &&
                        typeof formik.errors.team === "string"
                          ? formik.errors.team
                          : undefined
                      }
                    />
                  )}
                />
              </td> */}
              {/* User Dropdown - Visible */}
              <td className="py-2 px-3">
                <Autocomplete
                  size="small"
                  options={userOptions || []}
                  autoHighlight
                  loading={userDropdownLoading}
                  disabled={userDropdownLoading || !formik?.values?.team}
                  value={formik?.values?.user}
                  onChange={(_, val) => formik.setFieldValue("user", val)}
                  onBlur={() => formik.setFieldTouched("user", true)}
                  getOptionLabel={(option) => {
                    if (!option) return "";
                    const firstName = option?.first_name || "";
                    const lastName = option?.last_name || "";
                    const username = option?.username || "";
                    const name = `${firstName} ${lastName}`.trim();
                    return name ? `${name} (${username})` : username;
                  }}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => {
                        if (!opt) return "";
                        const firstName = opt?.first_name || "";
                        const lastName = opt?.last_name || "";
                        const username = opt?.username || "";
                        const name = `${firstName} ${lastName}`.trim();
                        return name ? `${name} (${username})` : username;
                      }
                    )
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select user"
                      sx={autocompleteDropdownStyles.inputSx}
                      error={formik.touched.user && Boolean(formik.errors.user)}
                      helperText={
                        formik.touched.user &&
                        typeof formik.errors.user === "string"
                          ? formik.errors.user
                          : undefined
                      }
                    />
                  )}
                />
              </td>

              <td className="py-2 px-3">
                {/* Reusable submit button with spinner */}
                <SubmitButton 
                  isSubmitting={loading} 
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    formik?.handleSubmit();
                  }} 
                  style={{width:'100%'}}
                />
                
              </td>
            </tr>

            {/* Existing Rows */}
            {listLoading ? (
              <tr>
                <td colSpan={3} className="text-center py-4">
                  <Spinner
                    animation="border"
                    variant="primary"
                    size="sm"
                    className="me-2"
                  />
                  Loading list...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-4 text-muted">
                  No configurations found
                </td>
              </tr>
            ) : (
              list.map((row: any, idx: number) => {
                const firstName = row?.user?.first_name || "";
                const lastName = row?.user?.last_name || "";
                const username = row?.user?.username || "";
                const name = `${firstName} ${lastName}`.trim();
                const displayName = name ? `${name} (${username})` : username;
                
                return (
                  <tr
                    key={row.id || row.configure_warehouse_id || idx}
                    className="border-bottom"
                  >
                    <td className="py-2 px-3">{idx + 1}</td>
                    <td className="py-2 px-3">{displayName}</td>
                    <td style={{ textAlign: "center" }} className="py-2 px-3">
                      <IconButton
                        onClick={() => handleDelete(row?.configure_warehouse_id)}
                        size="small"
                        color="error"
                        aria-label="Delete row"
                        disabled={deleteLoading === row?.configure_warehouse_id}
                      >
                        {deleteLoading === row?.configure_warehouse_id ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            style={{ color: "red", width: 20, height: 20 }}
                          />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-center gap-3 py-3">
        {/* <Button
          variant="light"
          onClick={() => navigate("/warehouse-list")}
          style={{
            backgroundColor: "white",
            border: "1px solid #ced4da",
            color: "#212529",
          }}
        >
          Cancel
        </Button> */}
        <CancelBackButton to={"/warehouse-list"}/>
        

      </div>
    </PageContainer>
  );
};

export default ConfigureWarehouse;
